import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import Image from "../elements/Image";
import Button from "../form/Button";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { wishListManage } from "../../services/wishlistService";
import ReviewRatingModal from "../common/ReviewRatingModal";
import ReviewPreview from "../common/ReviewPreview";
import RatingStars from "../common/RatingStars";
import {
  organizationFollow,
  organizationUnfollow,
} from "../../services/followService";

const OrganizationItem = ({
  data,
  addToFavoriteResponse = () => {},
  getResponse = () => {},
}) => {
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const { lang } = useSelector((state) => state?.langProvider);
  const { session } = useSelector((state) => state?.authProvider);
  const { data: organizationData } = useSelector(
    (state) => state?.organization?.selectedOrganizationDetails
  );
  const { data: usrData } = useSelector((state) => state?.profile);
  const navigate = useNavigate();

  const { mutate: wishlistManage, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        type: "wishlist-organization",
        ...val,
      };
      return await wishListManage(payload);
    },
    onSuccess: (res) => {
      addToFavoriteResponse(res);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleFavourite = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!usrData) {
      toast.info("Please login to manage your wishlist");
      navigate("/login");
      return;
    }
    wishlistManage({
      referenceId: data?._id,
      user: usrData?._id,
      organization: organizationData?._id,
    });
  };

  const handleOpenReviewModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReviewModalOpen(true);
  };

  const { mutate: manageFollowHandler, isPending } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        fromId: session?.user?._id,
        toId: data?._id,
        referenceType: "organization",
      };
      if (data?.isFollowing) {
        const res = await organizationUnfollow(payload);
        return res;
      } else {
        const res = await organizationFollow(payload);
        return res;
      }
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        getResponse({ ...res, data: { ...res?.data, _id: data?._id } });
        toast.success(res?.message);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
  });

  return (
    <div
      onClick={() => navigate(`/${lang}${data?.link}` || "#")}
      className="w-full rounded-xl overflow-hidden shadow-md relative transition-all duration-300 border hover:border-gradient-red bg-white"
    >
      {/* Image Slider Section */}
      <div className="relative w-full z-[1]">
        {data?.images?.length > 0 ? (
          data.images.length > 1 ? (
            <Swiper
              slidesPerView={1}
              autoplay={{
                delay: 2500,
                disableOnInteraction: false,
              }}
              pagination={{ clickable: true }}
              breakpoints={{
                320: { slidesPerView: 1 },
              }}
              modules={[Autoplay, Pagination]}
              className="w-full organizationSlider"
            >
              {data.images.map((img, index) => (
                <SwiperSlide key={index}>
                  <div className="w-full aspect-[7/5]">
                    <Image src={img} classes="w-full h-full object-cover" />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="w-full aspect-[7/5]">
              <Image
                src={data?.images[0]}
                classes="w-full h-full object-cover"
              />
            </div>
          )
        ) : (
          <div className="w-full aspect-[7/5] bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col items-center justify-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md mb-3">
              <i className="fa-solid fa-building text-2xl text-indigo-600"></i>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-gray-700 mb-1">
                {data?.organizationTitle
                  ? data.organizationTitle.length > 20
                    ? data.organizationTitle.substring(0, 20) + "..."
                    : data.organizationTitle
                  : "Organization"}
              </div>
              <div className="text-xs text-gray-500">
                {data?.category || "Company Profile"}
              </div>
            </div>
          </div>
        )}

        {/* Follow/Wishlist Button */}
        <div className="absolute right-3 top-3 z-[4]">
          <Button
            buttonFunction={handleFavourite}
            buttonIcon={
              data?.isWishlist ? "fa-solid fa-heart" : "fa-regular fa-heart"
            }
            buttonIconPosition="left"
            buttonClasses="w-9 aspect-square bg-white rounded-full flex items-center justify-center text-gradient-red"
          />
        </div>

        {/* Following Status Badge */}
        {/* {data?.isFollowing && (
          <div className="absolute left-3 top-3 z-[4]">
            <div className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-medium flex items-center gap-1">
              <i className="fa-solid fa-check"></i>
              Following
            </div>
          </div>
        )} */}
      </div>

      {/* Content Section */}
      <div className="py-5 px-5 relative">
        {/* Organization Name with Following Status */}
        {data?.organizationTitle && (
          <div className="mb-4">
            <div className="text-xl text-deep-purple font-bold line-clamp-2 mb-2 uppercase">
              {data?.organizationTitle}
            </div>

            {/* Following Status Indicator */}
            {/* <div className="flex items-center gap-2">
              {data?.isFollowing || data?.isWishlist ? (
                <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  <i className="fa-solid fa-user-check"></i>
                  Following
                </div>
              ) : (
                <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                  <i className="fa-regular fa-user-plus"></i>
                  Not Following
                </div>
              )}
            </div> */}
          </div>
        )}

        {/* Rating Section - if available */}
        {data?.rating && data.rating > 0 && (
          <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100">
            <RatingStars
              rating={data.rating}
              totalReviews={data.totalReviews || data.reviewCount || 0}
              showCount={true}
              size="lg"
            />

            {/* Write Review Button */}
            <Button
              buttonFunction={handleOpenReviewModal}
              buttonIcon="fa-regular fa-star"
              buttonIconPosition="left"
              buttonLabel="Review"
              buttonClasses="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-medium rounded-lg transition-all duration-200 border border-amber-200"
            />
          </div>
        )}

        {/* Category - if available */}
        {data?.category && (
          <div className="text-text-secondary text-base mb-2 flex items-center gap-3">
            <span className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/10">
              <i className="fa-regular fa-tag text-primary"></i>
            </span>
            {data?.category}
          </div>
        )}

        {/* Location - if available */}
        {data?.location && (
          <div className="text-text-secondary text-base mb-2 flex items-center gap-3">
            <span className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/10">
              <i className="fa-regular fa-location-dot text-primary"></i>
            </span>
            {data?.location}
          </div>
        )}

        {/* Established Year - if available */}
        {data?.establishedYear && (
          <div className="text-text-secondary text-base mb-2 flex items-center gap-3">
            <span className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/10">
              <i className="fa-regular fa-calendar text-primary"></i>
            </span>
            Est. {data?.establishedYear}
          </div>
        )}

        {/* Employee Count - if available */}
        {data?.employeeCount && (
          <div className="text-text-secondary text-base mb-2 flex items-center gap-3">
            <span className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/10">
              <i className="fa-regular fa-users text-primary"></i>
            </span>
            {data?.employeeCount} employees
          </div>
        )}

        {/* Description - if available */}
        {data?.description && (
          <div className="text-text-secondary text-sm font-medium mb-4 line-clamp-3">
            {data?.description}
          </div>
        )}

        {/* Latest Review - if available */}
        {data?.latestReview && <ReviewPreview review={data.latestReview} />}

        {/* Tags - if available */}
        {data?.tags?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {data?.tags.slice(0, 2).map((tag, index) => (
              <div
                key={index}
                className="h-8 rounded-full px-4 flex items-center justify-center text-xs font-medium border border-purple-400 text-purple-700"
              >
                {tag}
              </div>
            ))}
            {data?.tags.length > 2 && (
              <div className="h-8 aspect-square rounded-full border border-text-secondary flex items-center justify-center text-black text-xs font-medium">
                +{data?.tags.length - 2}
              </div>
            )}
          </div>
        )}

        {/* Website - if available */}
        {data?.website && (
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/10">
              <i className="fa-regular fa-globe text-primary"></i>
            </span>
            <a
              href={
                data.website.startsWith("http")
                  ? data.website
                  : `https://${data.website}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary text-sm hover:underline truncate"
              onClick={(e) => e.stopPropagation()}
            >
              {data.website}
            </a>
          </div>
        )}

        {/* Minimum Rate - if available */}
        {data?.organizationMinRate && (
          <div className="text-deep-purple text-sm uppercase">
            From{" "}
            <span className="text-deep-purple text-2xl uppercase font-bold">
              € {data?.organizationMinRate}
            </span>
          </div>
        )}

        <Button
          buttonLabel={
            !data?.isFollowing
              ? isPending
                ? "Following..."
                : "Follow"
              : isPending
              ? "Unfollowing..."
              : "Unfollow"
          }
          buttonIcon={
            !data?.isFollowing
              ? "fa-light fa-user-plus"
              : "fa-light fa-user-minus"
          }
          buttonIconClass={"text-deep-purple"}
          buttonClasses={"w-full bg-transparent border-deep-purple mt-6"}
          buttonLabelClasses={"text-deep-purple"}
          buttonFunction={(e) => {
            e.stopPropagation();
            manageFollowHandler();
          }}
          {...(isPending && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
        />
      </div>

      {/* Review Rating Modal */}
      <ReviewRatingModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        organization={data}
      />
    </div>
  );
};

export default OrganizationItem;
